/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { UndergroundInfrastructureType, UndergroundInfrastructureConfig } from '../types/underground';

export const UNDERGROUND_INFRASTRUCTURE: Record<UndergroundInfrastructureType, UndergroundInfrastructureConfig> = {
  // Level 1: Data Distribution Infrastructure
  [UndergroundInfrastructureType.DataConduit]: {
    type: UndergroundInfrastructureType.DataConduit,
    layer: -1,
    cost: 50,
    name: 'Fiber Optic Conduit',
    description: 'High-speed data transmission pipeline connecting network infrastructure',
    dimensions: { width: 1, height: 0.3, depth: 1 },
    specifications: {
      dataCapacity: 100,      // 100 Gbps
      powerConsumption: 0.5,  // 0.5 kW
      heatOutput: 0.1,        // 0.1 kW
      maintenanceCost: 2,     // $2/tick
      reliability: 99.9,      // 99.9% uptime
      latency: 0.1           // 0.1ms
    },
    requirements: {
      layer: -1,
      powerConnection: true,
      dataConnection: false,
      coolingRequired: false
    },
    effects: {
      complianceBonus: 1,
      threatReduction: 0,
      efficiencyBonus: 2,
      capacityMultiplier: 1.1
    },
    visual: {
      mesh: 'conduit',
      material: 'metallic',
      color: '#4a5568',
      animated: true
    }
  },

  [UndergroundInfrastructureType.NetworkSwitch]: {
    type: UndergroundInfrastructureType.NetworkSwitch,
    layer: -1,
    cost: 200,
    name: 'Core Network Switch',
    description: 'High-performance network switch for data distribution',
    dimensions: { width: 1, height: 1, depth: 2 },
    specifications: {
      dataCapacity: 400,      // 400 Gbps
      powerConsumption: 2,    // 2 kW
      heatOutput: 1.5,        // 1.5 kW
      maintenanceCost: 8,     // $8/tick
      reliability: 99.5,      // 99.5% uptime
      latency: 0.05          // 0.05ms
    },
    requirements: {
      layer: -1,
      adjacentTo: [UndergroundInfrastructureType.DataConduit],
      powerConnection: true,
      dataConnection: true,
      coolingRequired: false
    },
    effects: {
      complianceBonus: 3,
      threatReduction: 1,
      efficiencyBonus: 4,
      capacityMultiplier: 1.3
    },
    visual: {
      mesh: 'network_switch',
      material: 'metallic',
      color: '#2b6cb0',
      emissive: true,
      animated: true
    }
  },

  [UndergroundInfrastructureType.DistributionPanel]: {
    type: UndergroundInfrastructureType.DistributionPanel,
    layer: -1,
    cost: 150,
    name: 'Data Distribution Panel',
    description: 'Central hub for routing data to surface infrastructure',
    dimensions: { width: 2, height: 1.5, depth: 1 },
    specifications: {
      dataCapacity: 200,      // 200 Gbps
      powerConsumption: 1.5,  // 1.5 kW
      heatOutput: 1,          // 1 kW
      maintenanceCost: 5,     // $5/tick
      reliability: 99.3,      // 99.3% uptime
      latency: 0.2           // 0.2ms
    },
    requirements: {
      layer: -1,
      powerConnection: true,
      dataConnection: true,
      coolingRequired: false
    },
    effects: {
      complianceBonus: 2,
      threatReduction: 0,
      efficiencyBonus: 3,
      capacityMultiplier: 1.2
    },
    visual: {
      mesh: 'distribution_panel',
      material: 'metallic',
      color: '#3182ce',
      animated: false
    }
  },

  [UndergroundInfrastructureType.FiberBackbone]: {
    type: UndergroundInfrastructureType.FiberBackbone,
    layer: -1,
    cost: 100,
    name: 'Fiber Optic Backbone',
    description: 'Primary high-capacity data artery for the network',
    dimensions: { width: 1, height: 0.5, depth: 1 },
    specifications: {
      dataCapacity: 1000,     // 1 Tbps
      powerConsumption: 1,    // 1 kW
      heatOutput: 0.5,        // 0.5 kW
      maintenanceCost: 4,     // $4/tick
      reliability: 99.99,     // 99.99% uptime
      latency: 0.01          // 0.01ms
    },
    requirements: {
      layer: -1,
      powerConnection: true,
      dataConnection: false,
      coolingRequired: false
    },
    effects: {
      complianceBonus: 4,
      threatReduction: 2,
      efficiencyBonus: 6,
      capacityMultiplier: 1.5
    },
    visual: {
      mesh: 'fiber_backbone',
      material: 'metallic',
      color: '#065666',
      animated: true
    }
  },

  [UndergroundInfrastructureType.NetworkRack]: {
    type: UndergroundInfrastructureType.NetworkRack,
    layer: -1,
    cost: 300,
    name: 'Network Equipment Rack',
    description: 'Housing for switches, routers, and network equipment',
    dimensions: { width: 2, height: 3, depth: 1 },
    specifications: {
      dataCapacity: 600,      // 600 Gbps
      powerConsumption: 3,    // 3 kW
      heatOutput: 2.5,        // 2.5 kW
      maintenanceCost: 12,    // $12/tick
      reliability: 99.4,      // 99.4% uptime
      latency: 0.08          // 0.08ms
    },
    requirements: {
      layer: -1,
      powerConnection: true,
      dataConnection: true,
      coolingRequired: true
    },
    effects: {
      complianceBonus: 5,
      threatReduction: 1,
      efficiencyBonus: 5,
      capacityMultiplier: 1.4
    },
    visual: {
      mesh: 'network_rack',
      material: 'metallic',
      color: '#1e3a8a',
      emissive: true,
      animated: true
    }
  },

  // Level 2: Core Infrastructure
  [UndergroundInfrastructureType.ServerRack]: {
    type: UndergroundInfrastructureType.ServerRack,
    layer: -2,
    cost: 800,
    name: 'Enterprise Server Rack',
    description: 'High-density computing infrastructure with advanced processing capabilities',
    dimensions: { width: 2, height: 4, depth: 1 },
    specifications: {
      dataCapacity: 1000,     // 1 Tbps
      powerConsumption: 15,   // 15 kW
      heatOutput: 12,         // 12 kW
      maintenanceCost: 25,    // $25/tick
      reliability: 99.5,      // 99.5% uptime
      latency: 0.5           // 0.5ms
    },
    requirements: {
      layer: -2,
      adjacentTo: [UndergroundInfrastructureType.CoolingSystem],
      powerConnection: true,
      dataConnection: true,
      coolingRequired: true
    },
    effects: {
      complianceBonus: 8,
      threatReduction: 2,
      efficiencyBonus: 5,
      capacityMultiplier: 2.0
    },
    visual: {
      mesh: 'server_rack',
      material: 'metallic',
      color: '#2d3748',
      emissive: true,
      animated: true
    }
  },

  [UndergroundInfrastructureType.CoolingSystem]: {
    type: UndergroundInfrastructureType.CoolingSystem,
    layer: -2,
    cost: 600,
    name: 'Liquid Cooling System',
    description: 'Advanced thermal management for critical infrastructure',
    dimensions: { width: 2, height: 3, depth: 2 },
    specifications: {
      dataCapacity: 0,
      powerConsumption: 8,    // 8 kW
      heatOutput: -20,        // Removes 20 kW
      maintenanceCost: 15,    // $15/tick
      reliability: 98.5,
      latency: 0
    },
    requirements: {
      layer: -2,
      powerConnection: true,
      dataConnection: false,
      coolingRequired: false
    },
    effects: {
      complianceBonus: 3,
      threatReduction: 5,      // Prevents overheating threats
      efficiencyBonus: 3,
      capacityMultiplier: 1.2
    },
    visual: {
      mesh: 'cooling_system',
      material: 'metallic',
      color: '#3182ce',
      animated: true
    }
  },

  [UndergroundInfrastructureType.PowerDistribution]: {
    type: UndergroundInfrastructureType.PowerDistribution,
    layer: -2,
    cost: 400,
    name: 'Power Distribution Unit',
    description: 'Centralized power management and distribution for underground infrastructure',
    dimensions: { width: 1, height: 2, depth: 1 },
    specifications: {
      dataCapacity: 0,
      powerConsumption: 0.5,  // 0.5 kW overhead
      heatOutput: 0.3,        // 0.3 kW
      maintenanceCost: 8,     // $8/tick
      reliability: 99.8,
      latency: 0
    },
    requirements: {
      layer: -2,
      powerConnection: true,
      dataConnection: false,
      coolingRequired: false
    },
    effects: {
      complianceBonus: 2,
      threatReduction: 3,      // Reduces power-related threats
      efficiencyBonus: 4,
      capacityMultiplier: 1.1
    },
    visual: {
      mesh: 'power_distribution',
      material: 'metallic',
      color: '#f59e0b',
      emissive: false
    }
  },

  [UndergroundInfrastructureType.SecurityVault]: {
    type: UndergroundInfrastructureType.SecurityVault,
    layer: -2,
    cost: 2000,
    name: 'Hardened Security Vault',
    description: 'Physical security for critical data assets with advanced protection systems',
    dimensions: { width: 3, height: 2, depth: 3 },
    specifications: {
      dataCapacity: 500,      // 500 Gbps secure storage
      powerConsumption: 5,    // 5 kW
      heatOutput: 2,          // 2 kW
      maintenanceCost: 50,    // $50/tick
      reliability: 99.99,     // 99.99% uptime
      latency: 1.0           // 1ms (additional security overhead)
    },
    requirements: {
      layer: -2,
      powerConnection: true,
      dataConnection: true,
      coolingRequired: false
    },
    effects: {
      complianceBonus: 15,
      threatReduction: 10,
      efficiencyBonus: 0,
      capacityMultiplier: 1.5
    },
    visual: {
      mesh: 'security_vault',
      material: 'metallic',
      color: '#1a202c',
      emissive: false
    }
  },

  [UndergroundInfrastructureType.BackupGenerator]: {
    type: UndergroundInfrastructureType.BackupGenerator,
    layer: -2,
    cost: 1200,
    name: 'Diesel Backup Generator',
    description: 'Emergency power supply for critical infrastructure',
    dimensions: { width: 3, height: 2, depth: 2 },
    specifications: {
      dataCapacity: 0,
      powerConsumption: -50,   // Generates 50 kW
      heatOutput: 25,          // 25 kW heat
      maintenanceCost: 30,    // $30/tick
      reliability: 99.7,
      latency: 0
    },
    requirements: {
      layer: -2,
      powerConnection: false,
      dataConnection: false,
      coolingRequired: true
    },
    effects: {
      complianceBonus: 5,
      threatReduction: 8,      // Eliminates power outage threats
      efficiencyBonus: 2,
      capacityMultiplier: 1.3
    },
    visual: {
      mesh: 'backup_generator',
      material: 'metallic',
      color: '#4a5568',
      animated: false
    }
  },

  [UndergroundInfrastructureType.UPSUnit]: {
    type: UndergroundInfrastructureType.UPSUnit,
    layer: -2,
    cost: 500,
    name: 'Uninterruptible Power Supply',
    description: 'Battery backup for instant power during outages',
    dimensions: { width: 1, height: 2, depth: 1 },
    specifications: {
      dataCapacity: 0,
      powerConsumption: -5,    // Provides 5 kW
      heatOutput: 1,           // 1 kW
      maintenanceCost: 10,    // $10/tick
      reliability: 99.9,
      latency: 0
    },
    requirements: {
      layer: -2,
      powerConnection: true,
      dataConnection: false,
      coolingRequired: false
    },
    effects: {
      complianceBonus: 3,
      threatReduction: 5,      // Bridges power gaps
      efficiencyBonus: 1,
      capacityMultiplier: 1.1
    },
    visual: {
      mesh: 'ups_unit',
      material: 'metallic',
      color: '#10b981',
      emissive: false
    }
  },

  // Vertical Infrastructure
  [UndergroundInfrastructureType.VerticalShaft]: {
    type: UndergroundInfrastructureType.VerticalShaft,
    layer: 0, // Spans all layers
    cost: 300,
    name: 'Infrastructure Access Shaft',
    description: 'Vertical conduit connecting surface to underground layers',
    dimensions: { width: 1, height: 24, depth: 1 }, // Spans all layers
    specifications: {
      dataCapacity: 0,
      powerConsumption: 1,    // 1 kW for lighting/ventilation
      heatOutput: 0.5,
      maintenanceCost: 10,
      reliability: 99.0,
      latency: 0
    },
    requirements: {
      layer: 0,
      powerConnection: true,
      dataConnection: false,
      coolingRequired: false
    },
    effects: {
      complianceBonus: 2,
      threatReduction: 1,
      efficiencyBonus: 1,
      capacityMultiplier: 1.0
    },
    visual: {
      mesh: 'vertical_shaft',
      material: 'concrete',
      color: '#718096',
      animated: false
    }
  },

  [UndergroundInfrastructureType.CableTray]: {
    type: UndergroundInfrastructureType.CableTray,
    layer: 0, // Can be placed in any layer
    cost: 80,
    name: 'Cable Management Tray',
    description: 'Organized routing for power and data cables between layers',
    dimensions: { width: 1, height: 0.2, depth: 1 },
    specifications: {
      dataCapacity: 0,
      powerConsumption: 0,    // Passive component
      heatOutput: 0,
      maintenanceCost: 3,
      reliability: 99.5,
      latency: 0
    },
    requirements: {
      layer: 0,
      powerConnection: false,
      dataConnection: false,
      coolingRequired: false
    },
    effects: {
      complianceBonus: 1,
      threatReduction: 1,
      efficiencyBonus: 2,
      capacityMultiplier: 1.05
    },
    visual: {
      mesh: 'cable_tray',
      material: 'metallic',
      color: '#9ca3af',
      animated: false
    }
  },

  [UndergroundInfrastructureType.CoolingPipe]: {
    type: UndergroundInfrastructureType.CoolingPipe,
    layer: 0, // Can be placed in any layer
    cost: 60,
    name: 'Liquid Cooling Pipe',
    description: 'Distribution network for cooling systems between equipment',
    dimensions: { width: 0.5, height: 0.3, depth: 1 },
    specifications: {
      dataCapacity: 0,
      powerConsumption: 0.2,  // Pump power
      heatOutput: -2,         // Transports heat away
      maintenanceCost: 4,
      reliability: 98.0,
      latency: 0
    },
    requirements: {
      layer: 0,
      powerConnection: true,
      dataConnection: false,
      coolingRequired: false
    },
    effects: {
      complianceBonus: 1,
      threatReduction: 2,
      efficiencyBonus: 1,
      capacityMultiplier: 1.1
    },
    visual: {
      mesh: 'cooling_pipe',
      material: 'metallic',
      color: '#2563eb',
      animated: true
    }
  },

  [UndergroundInfrastructureType.PowerConduit]: {
    type: UndergroundInfrastructureType.PowerConduit,
    layer: 0, // Can be placed in any layer
    cost: 70,
    name: 'High-Voltage Power Conduit',
    description: 'Protected power distribution channel for high-capacity equipment',
    dimensions: { width: 0.8, height: 0.4, depth: 1 },
    specifications: {
      dataCapacity: 0,
      powerConsumption: 0.1,  // Minimal loss
      heatOutput: 0.1,        // Small heating from resistance
      maintenanceCost: 3,
      reliability: 99.7,
      latency: 0
    },
    requirements: {
      layer: 0,
      powerConnection: false,
      dataConnection: false,
      coolingRequired: false
    },
    effects: {
      complianceBonus: 1,
      threatReduction: 1,
      efficiencyBonus: 1,
      capacityMultiplier: 1.05
    },
    visual: {
      mesh: 'power_conduit',
      material: 'metallic',
      color: '#dc2626',
      animated: false
    }
  }
};

// Research requirements for unlocking underground infrastructure
export const UNDERGROUND_RESEARCH_REQUIREMENTS = {
  // Basic underground access
  underground_access: {
    techId: 'tech_underground_access',
    name: 'Underground Infrastructure',
    description: 'Unlock ability to build underground data infrastructure',
    cost: 500,
    duration: 10,
    unlocks: [
      UndergroundInfrastructureType.VerticalShaft,
      UndergroundInfrastructureType.DataConduit,
      UndergroundInfrastructureType.NetworkSwitch
    ]
  },

  // Advanced networking
  advanced_networking: {
    techId: 'tech_advanced_networking',
    name: 'Advanced Network Infrastructure',
    description: 'High-speed fiber optic backbones and core network equipment',
    cost: 800,
    duration: 15,
    unlocks: [
      UndergroundInfrastructureType.FiberBackbone,
      UndergroundInfrastructureType.NetworkRack,
      UndergroundInfrastructureType.DistributionPanel
    ],
    prerequisites: ['tech_underground_access']
  },

  // Core infrastructure
  core_infrastructure: {
    techId: 'tech_core_infrastructure',
    name: 'Core Data Infrastructure',
    description: 'Enterprise-grade servers and computing systems',
    cost: 1500,
    duration: 20,
    unlocks: [
      UndergroundInfrastructureType.ServerRack,
      UndergroundInfrastructureType.PowerDistribution,
      UndergroundInfrastructureType.CoolingSystem
    ],
    prerequisites: ['tech_underground_access']
  },

  // Security infrastructure
  security_infrastructure: {
    techId: 'tech_security_infrastructure',
    name: 'Physical Security Infrastructure',
    description: 'Hardened vaults and secure data storage systems',
    cost: 2000,
    duration: 25,
    unlocks: [
      UndergroundInfrastructureType.SecurityVault
    ],
    prerequisites: ['tech_core_infrastructure']
  },

  // Power redundancy
  power_redundancy: {
    techId: 'tech_power_redundancy',
    name: 'Power Redundancy Systems',
    description: 'Backup power generation and uninterruptible power supplies',
    cost: 1200,
    duration: 18,
    unlocks: [
      UndergroundInfrastructureType.BackupGenerator,
      UndergroundInfrastructureType.UPSUnit
    ],
    prerequisites: ['tech_core_infrastructure']
  }
};

// Underground building categories for organization
export const UNDERGROUND_CATEGORIES = {
  NETWORKING: [
    UndergroundInfrastructureType.DataConduit,
    UndergroundInfrastructureType.NetworkSwitch,
    UndergroundInfrastructureType.FiberBackbone,
    UndergroundInfrastructureType.NetworkRack,
    UndergroundInfrastructureType.DistributionPanel
  ],
  COMPUTING: [
    UndergroundInfrastructureType.ServerRack
  ],
  COOLING: [
    UndergroundInfrastructureType.CoolingSystem,
    UndergroundInfrastructureType.CoolingPipe
  ],
  POWER: [
    UndergroundInfrastructureType.PowerDistribution,
    UndergroundInfrastructureType.BackupGenerator,
    UndergroundInfrastructureType.UPSUnit,
    UndergroundInfrastructureType.PowerConduit
  ],
  SECURITY: [
    UndergroundInfrastructureType.SecurityVault
  ],
  INFRASTRUCTURE: [
    UndergroundInfrastructureType.VerticalShaft,
    UndergroundInfrastructureType.CableTray
  ]
};